import { useAuth0 } from '@auth0/auth0-react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Button, Grid, Stack, styled, Typography } from '@mui/material';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CircularIntegration from '../../components/ProgressComplete';
import { updateStatus as updateAvailStatus } from '../../redux/AvailabilitySlice';
import { updateStatus as updateBookingStatus } from '../../redux/BookingSlice';
import SimpleSnackbar from '../SnackBar/SimpleSnackbar';
import sendNotification from '../../utils/sendNotification';
import './Calendar.css';
import SaveCalendar from './SaveCalender';
import SignSchoolDocs from '../BasicComponents/SignSchoolDocs';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const SchoolLogoImg = styled('img')(() => ({
    width: '200px',
    height: '100%',
}));

const ItemGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    margin: 0,
}));

//Made with the help of this tutorial https://programmingwithmosh.com/react/build-a-react-calendar-component-from-scratch/

const ViewOnHold = props => {
    let history = useHistory();
    // const classes = useStyles();
    const dispatch = useDispatch();

    const { getAccessTokenSilently } = useAuth0();

    const [state, setState] = useState({
        loading: true,
        booking: {},
        openSnack: false,
        snackBarMessage: '',
        snackSeverity: 'success',
    });

    const [signDocsOpen, setSignDocsOpen] = useState(false);
    const [schoolDocsInfo, setSchoolDocsInfo] = useState({});
    const [cached, setCached] = useState({});

    const navigateToDetails = uniqueId => {
        history.push(
            history.location.pathname + '/teacher-booking/' + uniqueId
        );
    };

    useEffect(() => {
        if (props.booking.sk1 !== 'undefined') {
            console.log('sk1 = ', props.booking.sk1);
            async function fetchBooking() {
                const token = await getAccessTokenSilently({
                    scope: process.env.REACT_APP_scope,
                });

                const response = await axios.get('/getTeacherBookings', {
                    params: {
                        'sk1': props.booking.sk1,
                    },
                    baseURL: process.env.REACT_APP_API_GATEWAY,
                    url: '/getTeacherBookings',
                    timeout: 30000,
                    headers: {
                        authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                if (response.status === 200) {
                    setState({
                        ...state,
                        loading: false,
                        booking: response.data.booking.Message.Data[0],
                    });
                } else {
                    // setState({
                    //     ...state,
                    //     snackBarMessage: 'Failed to get Booking Details',
                    //     openSnack: true,
                    // });
                    sendNotification(
                        'error',
                        6000,
                        'Failed to get Booking Details'
                    );
                }
            }
            fetchBooking();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.booking.sk1]);

    const confirmBooking = async (approve, date, id) => {
        if (approve === undefined) {
            approve = cached.approve;
            date = cached.date;
            id = cached.id;
        }
        var interimBooking = state.booking;
        interimBooking.status = 'Processing';
        setState({
            ...state,
            loading: false,
            booking: interimBooking,
        });
        const token = await getAccessTokenSilently({
            scope: process.env.REACT_APP_scope,
        });
        const data = {
            'request': { 'approve': approve.toString(), 'date': date },
        };

        axios
            .put('/confirmBooking', data.request, {
                baseURL: process.env.REACT_APP_API_GATEWAY,
                url: '/confirmBooking',
                timeout: 30000,
                headers: {
                    authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            })
            .then(response => {
                dispatch(
                    updateAvailStatus({
                        id: id,
                        status: response.data.Message.Data.availStatus,
                    })
                );

                if (response.data.Message.Code === 'Awaiting Sign') {
                    interimBooking.status = 'Pending';
                    setState({
                        ...state,
                        booking: interimBooking,
                    });
                    setSchoolDocsInfo({
                        docs: response.data.Message.Data.results,
                        logo: state.booking.schoolLogo,
                        name: state.booking.schoolName,
                    });
                    setSignDocsOpen(true);
                    setCached({
                        approve: approve,
                        date: date,
                        id: id,
                    });
                } else {
                    interimBooking.status =
                        response.data.Message.Data.bookingStatus;

                    dispatch(
                        updateBookingStatus({
                            id: response.data.Message.Data.bookingUniqueId,
                            status: response.data.Message.Data.bookingStatus,
                        })
                    );

                    setState({
                        ...state,
                        loading: false,
                        booking: interimBooking,
                        // snackBarMessage: 'Booking Updated',
                        // openSnack: true,
                        // snackSeverity: 'success',
                    });
                    sendNotification('success', 6000, 'Booking Updated');
                }
            })
            .catch(err => {
                console.log(err);
                interimBooking.status = 'Failed';
                setState({
                    ...state,
                    loading: false,
                    booking: interimBooking,
                    // snackBarMessage: 'Booking Failed to Update',
                    // openSnack: true,
                    // snackSeverity: 'error',
                });

                sendNotification('error', 6000, 'Booking Failed to Update');
            });
    };

    let content = <Typography variant="h1">Loading</Typography>;

    if (state.loading === true) {
        content = <Typography variant="h1">Loading</Typography>;
    } else {
        content = (
            <Grid container>
                <ItemGrid item xs={12}>
                    <Typography variant="h1">
                        Booking {state.booking.status}
                    </Typography>
                </ItemGrid>

                <ItemGrid item xs={12} sm={6}>
                    <SchoolLogoImg
                        src={state.booking.schoolLogo}
                        alt={`${state.booking.schoolName} School Logo`}
                    />
                </ItemGrid>
                <ItemGrid item xs={12} sm={6}>
                    <Typography variant="h2" sx={{ fontWeight: '400' }}>
                        {state.booking.schoolName}
                    </Typography>
                </ItemGrid>
                <ItemGrid item xs={12}>
                    <Stack
                        direction="row"
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <LocationOnIcon />
                        <a
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                window.open(
                                    `https://www.google.com/maps/search/?api=1&query=` +
                                        `${state.booking.schoolLocation
                                            .replace(/ /g, '+')
                                            .replace(/,/g, '%2C')
                                            .replace(/\//g, '%2F')}`
                                );
                            }}
                        >
                            <Typography variant="body1">
                                {state.booking.schoolLocation}
                            </Typography>
                        </a>
                    </Stack>
                </ItemGrid>
                <ItemGrid item xs={12}>
                    {' '}
                    <Typography variant="h4">
                        Date{': '}
                        {format(
                            parseISO(state.booking.startDate),
                            'EEEE, dd MMM yyyy'
                        )}
                    </Typography>
                </ItemGrid>
                <ItemGrid item xs={12}>
                    <Typography variant="h4">
                        Time{': '}
                        {format(parseISO(state.booking.startDate), 'h:mma')}
                        {' - '}
                        {format(parseISO(state.booking.endDate), 'h:mma')}
                    </Typography>
                </ItemGrid>

                {state.booking.status === 'Pending' ? (
                    <Stack
                        spacing={2}
                        direction="row"
                        sx={{ m: 2, width: '100%' }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <StyledButton
                            variant="outlined"
                            color="secondary"
                            size="large"
                            onClick={() => {
                                confirmBooking(
                                    true,
                                    state.booking.sk1,
                                    props.booking.uniqueId
                                );
                            }}
                            startIcon={
                                <CheckCircleOutlineIcon color="sexysuccess" />
                            }
                        >
                            Accept
                            {/* <CheckCircleOutlineIcon color="success"></CheckCircleOutlineIcon> */}
                        </StyledButton>
                        <StyledButton
                            data-cy="decline-booking"
                            color="secondary"
                            variant="contained"
                            size="large"
                            onClick={() => {
                                confirmBooking(
                                    false,
                                    state.booking.sk1,
                                    props.booking.uniqueId
                                );
                            }}
                            startIcon={
                                <RemoveCircleOutlineIcon color="sexyerror" />
                            }
                        >
                            {' '}
                            Decline
                            {/* <RemoveCircleOutlineIcon ></RemoveCircleOutlineIcon> */}
                        </StyledButton>
                    </Stack>
                ) : (
                    <Grid item xs={12} justifyContent="center" display="flex">
                        <CircularIntegration
                            loading={
                                state.booking.status === 'Processing'
                                    ? true
                                    : false
                            }
                            success={
                                state.booking.status === 'Approved'
                                    ? true
                                    : false
                            }
                            failed={
                                state.booking.status === 'Failed' ? true : false
                            }
                            successMessage={'Booking ' + state.booking.status}
                            failedMessage="Unable to Confirm Booking"
                        ></CircularIntegration>
                    </Grid>
                )}
                {state.booking.status === 'Approved' && (
                    <SaveCalendar booking={state.booking} />
                )}
                <ItemGrid item xs={12}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() =>
                            navigateToDetails(state.booking.uniqueId)
                        }
                    >
                        View Details
                    </Button>
                </ItemGrid>
                <ItemGrid item xs={12}>
                    <SimpleSnackbar
                        message={state.snackBarMessage}
                        open={state.openSnack}
                        severity={state.snackSeverity}
                    />
                </ItemGrid>
                {signDocsOpen && (
                    <SignSchoolDocs
                        setOpen={setSignDocsOpen}
                        schoolDocsInfo={schoolDocsInfo}
                        setNotif={(severity, autoHideDuration, description) => {
                            sendNotification(
                                severity,
                                autoHideDuration,
                                description
                            );
                        }}
                        confirmBooking={confirmBooking}
                    />
                )}
            </Grid>
        );
    }
    return <div>{content}</div>;
};

export default ViewOnHold;
